import React, { useState, useCallback } from 'react'
import { createLesson } from '../services/lessonsApi'
import { toast } from '../utils/toast'
import { formatError } from '../utils/errorHandler'
import './CreateLessonModal.css'

const GUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
const INVALID_FILENAME_CHARS = /[/\\:*?"<>|]/g

function CreateLessonModal({ onClose, onSuccess }) {
  const [inputMode, setInputMode] = useState('paste')
  const [lessonName, setLessonName] = useState('')
  const [content, setContent] = useState('')
  const [file, setFile] = useState(null)
  const [fileDisplayName, setFileDisplayName] = useState('')
  const [uploading, setUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isAbsorbCourse, setIsAbsorbCourse] = useState(false)
  const [absorbCourseId, setAbsorbCourseId] = useState('')
  const [passingScore, setPassingScore] = useState(80)
  const [passingScoreWarning, setPassingScoreWarning] = useState('')
  const [nameAutoPopulated, setNameAutoPopulated] = useState(false)

  const hasContent = content.trim().length > 0
  const hasFile = file !== null

  const handleTabSwitch = (mode) => {
    if (mode === inputMode) return
    setInputMode(mode)
    if (nameAutoPopulated) {
      setLessonName('')
      setNameAutoPopulated(false)
    }
    if (mode === 'paste') {
      setFile(null)
      setFileDisplayName('')
      const fileInput = document.getElementById('create-file-input')
      if (fileInput) fileInput.value = ''
    } else {
      setContent('')
    }
    setPassingScore(80)
    setPassingScoreWarning('')
  }

  const getFileNameWithoutExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.')
    return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename
  }

  const sanitizeFileName = (name) => {
    return name.replace(INVALID_FILENAME_CHARS, '').trim()
  }

  const extractPassingScore = (text) => {
    const requiredScoreMatch = text.match(/required_score:\s*(\d+)/)
    const totalQuestionsMatch = text.match(/total_questions:\s*(\d+)/)

    if (requiredScoreMatch && totalQuestionsMatch) {
      const required = parseInt(requiredScoreMatch[1], 10)
      const total = parseInt(totalQuestionsMatch[1], 10)
      if (total > 0) {
        return Math.round((required / total) * 100)
      }
    }
    return null
  }

  const extractTitleFromMarkdown = (text) => {
    try {
      const frontmatterMatch = text.match(/^---\s*\n([\s\S]*?)\n---\s*\n/)
      if (frontmatterMatch) {
        const frontmatter = frontmatterMatch[1]
        const titleMatch = frontmatter.match(/^title:\s*["']?([^"'\n]+)["']?/m)
        if (titleMatch && titleMatch[1]) {
          return titleMatch[1].trim()
        }
      }

      const h1Match = text.match(/^#\s+(.+)$/m)
      if (h1Match && h1Match[1]) {
        return h1Match[1].trim()
      }

      return null
    } catch (error) {
      console.error('Error extracting title from markdown:', error)
      return null
    }
  }

  const applyContentExtraction = useCallback((text) => {
    if (!lessonName.trim() || nameAutoPopulated) {
      const extractedTitle = extractTitleFromMarkdown(text)
      if (extractedTitle) {
        setLessonName(extractedTitle)
        setNameAutoPopulated(true)
      }
    }

    const calculatedScore = extractPassingScore(text)
    if (calculatedScore !== null) {
      setPassingScore(calculatedScore)
      setPassingScoreWarning('')
    } else {
      setPassingScore(80)
      setPassingScoreWarning('No quiz section detected in the lesson content. Defaulting to 80%.')
    }
  }, [lessonName, nameAutoPopulated])

  const handleContentChange = (e) => {
    setContent(e.target.value)
  }

  const handleContentPaste = (e) => {
    setTimeout(() => {
      const pasted = e.target.value
      if (pasted && pasted.trim().length > 0) {
        applyContentExtraction(pasted)
      }
    }, 0)
  }

  const handleClearContent = () => {
    setContent('')
    if (nameAutoPopulated) {
      setLessonName('')
      setNameAutoPopulated(false)
    }
    setPassingScore(80)
    setPassingScoreWarning('')
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      processFile(selectedFile)
    }
  }

  const processFile = async (selectedFile) => {
    if (!selectedFile.name.toLowerCase().endsWith('.md')) {
      toast.error('Only .md files are allowed')
      return
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5 MB')
      return
    }

    setFile(selectedFile)
    setFileDisplayName(selectedFile.name)

    try {
      const fileContent = await selectedFile.text()
      const extractedTitle = extractTitleFromMarkdown(fileContent)

      if (!lessonName.trim() || nameAutoPopulated) {
        if (extractedTitle) {
          setLessonName(extractedTitle)
        } else {
          const fileNameWithoutExt = getFileNameWithoutExtension(selectedFile.name)
          setLessonName(fileNameWithoutExt)
        }
        setNameAutoPopulated(true)
      }

      const calculatedScore = extractPassingScore(fileContent)
      if (calculatedScore !== null) {
        setPassingScore(calculatedScore)
        setPassingScoreWarning('')
      } else {
        setPassingScore(80)
        setPassingScoreWarning('No quiz section detected in the lesson file. Defaulting to 80%.')
      }
    } catch (error) {
      console.error('Error reading file:', error)
      const fileNameWithoutExt = getFileNameWithoutExtension(selectedFile.name)
      if (!lessonName.trim() || nameAutoPopulated) {
        setLessonName(fileNameWithoutExt)
        setNameAutoPopulated(true)
      }
    }
  }

  const handleRemoveFile = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setFile(null)
    setFileDisplayName('')
    if (nameAutoPopulated) {
      setLessonName('')
      setNameAutoPopulated(false)
    }
    setPassingScore(80)
    setPassingScoreWarning('')
    const fileInput = document.getElementById('create-file-input')
    if (fileInput) fileInput.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = e.dataTransfer.files
    if (droppedFiles.length > 0) {
      await processFile(droppedFiles[0])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!lessonName.trim()) {
      toast.error('Please enter a lesson name')
      return
    }

    if (inputMode === 'paste' && !hasContent) {
      toast.error('Please enter lesson content')
      return
    }
    if (inputMode === 'upload' && !hasFile) {
      toast.error('Please upload a file')
      return
    }

    if (isAbsorbCourse) {
      if (!absorbCourseId.trim()) {
        toast.error('Please enter an Absorb Course ID')
        return
      }
      if (!GUID_REGEX.test(absorbCourseId.trim())) {
        toast.error('Absorb Course ID must be a valid GUID (e.g., d5779be6-9322-4279-b055-1f5a1602c1cf)')
        return
      }
    }

    setUploading(true)

    try {
      const displayName = inputMode === 'upload'
        ? fileDisplayName
        : `${sanitizeFileName(lessonName) || 'lesson'}.md`

      const result = await createLesson(
        lessonName,
        displayName,
        inputMode === 'upload' ? file : null,
        isAbsorbCourse,
        absorbCourseId.trim(),
        passingScore,
        inputMode === 'paste' ? content : null
      )
      toast.success(`Lesson created successfully! Lesson Key: ${result.lessonKey}`, 5000)

      setInputMode('paste')
      setLessonName('')
      setNameAutoPopulated(false)
      setContent('')
      setFile(null)
      setFileDisplayName('')
      setIsAbsorbCourse(false)
      setAbsorbCourseId('')
      setPassingScore(80)
      setPassingScoreWarning('')
      const fileInput = document.getElementById('create-file-input')
      if (fileInput) fileInput.value = ''

      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (error) {
      const errorMessage = formatError(error)
      toast.error(errorMessage, 5000)
    } finally {
      setUploading(false)
    }
  }

  const hasValidInput = (inputMode === 'paste' && hasContent) || (inputMode === 'upload' && hasFile)

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Create New Lesson</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label>Content Source</label>
              <div className="content-tab-group">
                <button
                  type="button"
                  className={`content-tab ${inputMode === 'paste' ? 'active' : ''}`}
                  onClick={() => handleTabSwitch('paste')}
                  disabled={uploading}
                >
                  Paste Content
                </button>
                <button
                  type="button"
                  className={`content-tab ${inputMode === 'upload' ? 'active' : ''}`}
                  onClick={() => handleTabSwitch('upload')}
                  disabled={uploading}
                >
                  Upload File
                </button>
              </div>
              <span className="content-tab-hint">Switching tabs will clear unsaved content from the other tab</span>
            </div>

            {inputMode === 'paste' && (
              <div className="form-group">
                <textarea
                  id="create-content"
                  className="lesson-content-textarea"
                  value={content}
                  onChange={handleContentChange}
                  onPaste={handleContentPaste}
                  placeholder="Paste or type your lesson content here (Markdown supported)"
                  disabled={uploading}
                  rows={5}
                />
                {hasContent && (
                  <span className="clear-content-link" onClick={handleClearContent}>Clear</span>
                )}
              </div>
            )}

            {inputMode === 'upload' && (
              <div className="form-group">
                <div className="file-upload-wrapper file-upload-compact">
                  <input
                    id="create-file-input"
                    type="file"
                    accept=".md"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="file-input-hidden"
                  />
                  <label
                    htmlFor="create-file-input"
                    className={`file-upload-area ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {file ? (
                      <div className="file-upload-selected">
                        <div className="file-icon">📄</div>
                        <div className="file-details">
                          <div className="file-name">{file.name}</div>
                          <div className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                        </div>
                        <button
                          type="button"
                          className="file-remove-btn"
                          onClick={handleRemoveFile}
                          disabled={uploading}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="file-upload-placeholder">
                        <div className="upload-icon">📤</div>
                        <div className="upload-text">
                          <span className="upload-text-main">Click to upload</span>
                          <span className="upload-text-sub">or drag and drop</span>
                        </div>
                        <div className="upload-hint">Markdown (.md) files only, max 5 MB</div>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="create-lesson-name">Lesson Name *</label>
              <input
                id="create-lesson-name"
                type="text"
                value={lessonName}
                onChange={(e) => { setLessonName(e.target.value); setNameAutoPopulated(false) }}
                placeholder="Enter lesson name"
                disabled={uploading}
                required
              />
            </div>

            <div className="form-group absorb-toggle-group">
              <div className="absorb-toggle-row">
                <span className="absorb-toggle-text">Is Absorb Lesson?</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isAbsorbCourse}
                  className={`toggle-switch ${isAbsorbCourse ? 'toggle-on' : ''}`}
                  onClick={() => {
                    setIsAbsorbCourse(!isAbsorbCourse)
                    if (isAbsorbCourse) {
                      setAbsorbCourseId('')
                      setPassingScore(80)
                      setPassingScoreWarning('')
                    }
                  }}
                  disabled={uploading}
                >
                  <span className="toggle-knob" />
                </button>
              </div>
              {isAbsorbCourse && (
                <input
                  type="text"
                  value={absorbCourseId}
                  onChange={(e) => setAbsorbCourseId(e.target.value)}
                  placeholder="Absorb Course ID (e.g., d5779be6-9322-4279-b055-1f5a1602c1cf)"
                  disabled={uploading}
                  required
                  className={absorbCourseId && !GUID_REGEX.test(absorbCourseId.trim()) ? 'invalid-input' : ''}
                />
              )}
              {isAbsorbCourse && (
                <div className="form-group passing-score-group">
                  <label htmlFor="create-passing-score">
                    Passing Score (%)
                    <span className="info-icon" title="The minimum percentage score required to pass this lesson. Auto-calculated from the uploaded file when possible.">
                      i
                    </span>
                  </label>
                  <input
                    id="create-passing-score"
                    type="number"
                    min="0"
                    max="100"
                    value={passingScore}
                    onChange={(e) => setPassingScore(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
                    disabled={uploading}
                  />
                  {passingScoreWarning && (
                    <span className="passing-score-warning">{passingScoreWarning}</span>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={uploading || !lessonName || !hasValidInput || (isAbsorbCourse && (!absorbCourseId.trim() || !GUID_REGEX.test(absorbCourseId.trim())))}
              >
                {uploading ? 'Uploading...' : 'Create Lesson'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateLessonModal
